import { themeColors } from '@/theme/colors';
import { Button, Icon } from '@lego/connect-components-react';
import { Flex, IconButton, Text } from '@lego/klik-ui';
import { CircleXmarkSolid, StopSolid } from '@lego/klik-ui-icons';

type UploadedFileViewProps = {
  file: File;
  uploadProgress: number;
  uploadStartTime: number | null;
  isUploading: boolean;
  canUpload: boolean;
  onRemoveFile: () => void;
  onUpload: () => void;
  onCancel: () => void;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const formatTimeRemaining = (
  progress: number,
  startTime: number | null,
): string => {
  if (!startTime || progress <= 0 || progress >= 100) return '';
  const elapsed = (Date.now() - startTime) / 1000;
  const estimated = (elapsed / progress) * (100 - progress);
  if (estimated < 60) return `${Math.ceil(estimated)}s left`;
  return `${Math.ceil(estimated / 60)}m left`;
};

const UploadedFileView = ({
  file,
  uploadProgress,
  uploadStartTime,
  isUploading,
  canUpload,
  onRemoveFile,
  onUpload,
  onCancel,
}: UploadedFileViewProps) => {
  const uploadedBytes = (uploadProgress / 100) * file.size;
  const timeRemaining = formatTimeRemaining(uploadProgress, uploadStartTime);

  return (
    <Flex flexDirection="column" gap="16px" width="100%" flex="1">
      <Flex
        padding="24px"
        backgroundColor={themeColors.surface.background.base.muted}
        borderRadius="12px"
        position="relative"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        flex="1"
      >
        {!isUploading && (
          <IconButton
            aria-label="remove file"
            icon={<CircleXmarkSolid />}
            onClick={onRemoveFile}
            variant="ghost"
            color={themeColors.content.subtle}
            size="24px"
            style={{
              cursor: 'pointer',
              position: 'absolute',
              top: '12px',
              right: '12px',
            }}
          />
        )}
        <Flex
          flexDirection="column"
          gap="8px"
          alignItems="center"
          justifyContent="center"
        >
          <Icon
            name="xls"
            aria-label="Excel file icon"
            color="ds-color-interactive-primary-enabled"
            style={{ width: '48px', height: '48px' }}
          />
          <Flex flexDirection="column" alignItems="center" gap="2px">
            <Text className="label-sm-medium">{file.name}</Text>
            <Text
              className="label-xs-regular"
              color={themeColors.content.subtle}
            >
              {formatFileSize(file.size)}
            </Text>
          </Flex>
          {!isUploading && (
            <Button
              variant="primary"
              size="medium"
              onClick={onUpload}
              disabled={!canUpload}
            >
              Upload file
            </Button>
          )}
        </Flex>
      </Flex>
      {isUploading && (
        <Flex flexDirection="column" gap="8px" width="100%">
          <div
            style={{
              width: '100%',
              height: '4px',
              borderRadius: '4px',
              backgroundColor: themeColors.surface.background.base.muted,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${uploadProgress}%`,
                borderRadius: '4px',
                backgroundColor: themeColors.interactive.primary.enabled,
                transition: 'width 0.3s ease',
              }}
            />
          </div>
          <Flex justifyContent="space-between" alignItems="center" width="100%">
            <Flex flexDirection="column" gap="2px">
              <Flex alignItems="center" gap="6px">
                <Icon
                  name="loader"
                  aria-label="uploading"
                  size="small"
                  color="ds-color-interactive-primary-enabled"
                />
                <Text className="label-sm-medium">
                  Uploading… {uploadProgress}%
                </Text>
              </Flex>
              <Text
                className="label-xs-regular"
                color={themeColors.content.subtle}
              >
                {formatFileSize(uploadedBytes)} of {formatFileSize(file.size)}
                {timeRemaining && ` - ${timeRemaining}`}
              </Text>
            </Flex>
            <IconButton
              aria-label="cancel upload"
              icon={<StopSolid />}
              onClick={onCancel}
              variant="ghost"
              color={themeColors.interactive.primary.enabled}
              size="32px"
              style={{ cursor: 'pointer' }}
            />
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

export default UploadedFileView;
